// Drag.tsx
import { useEffect, useMemo, useRef } from "react";

import Sortable from "sortablejs";

export function useSortableProgress({
  dropRef,
  itemCount,
  onSort,
  dragDelayMs = 100,        // <-- add a tiny press-and-hold delay (mouse + touch)
  useHandle = false,        // <-- optional: only drag from a handle
}: {
  dropRef: React.RefObject<HTMLDivElement>;
  itemCount: number;
  onSort?: (ids: string[]) => void;
  dragDelayMs?: number;
  useHandle?: boolean;
}) {
  const sortableRef = useRef<Sortable | null>(null);
  const onSortRef = useRef<typeof onSort>();
  onSortRef.current = onSort;

  const dragState = useRef<{ dragging: boolean; suppressUntil: number }>({
    dragging: false,
    suppressUntil: 0,
  });

  const getId = useMemo(() => {
    const map = new WeakMap<object, string>();
    return (item: any) => {
      if (item?.id != null) return String(item.id);
      if (item?.uid != null) return String(item.uid);
      if (!map.has(item)) {
        const id =
          globalThis.crypto?.randomUUID?.() ??
          Math.random().toString(36).slice(2);
        map.set(item, id);
      }
      return map.get(item)!;
    };
  }, []);

  useEffect(() => {
    const el = dropRef.current;
    if (!el || sortableRef.current) return;

    sortableRef.current = new Sortable(el, {
      animation: 350,
      draggable: ".drop-card",
      dataIdAttr: "data-id",
      ghostClass: "sortable-ghost",
      dragClass: "sortable-drag",

      // === press & hold before drag starts ===
      delay: dragDelayMs,          // delay applies to both mouse and touch
      delayOnTouchOnly: false,     // keep true if you ONLY want it on touch

      // make tiny wiggles not start a drag (esp. with fallback)
      fallbackTolerance: 4,        // px movement required to initiate fallback drag

      // optional: only allow dragging from a handle inside the card
      ...(useHandle ? { handle: ".drag-handle" } : {}),

      onStart: () => {
        dragState.current.dragging = true;
      },
      onEnd: () => {
        dragState.current.dragging = false;
        dragState.current.suppressUntil = performance.now() + 250;
        const ids = sortableRef.current?.toArray() ?? [];
        onSortRef.current?.(ids);
      },
    });

    // swallow clicks that immediately follow a drag
    const cancelClick = (e: MouseEvent) => {
      const now = performance.now();
      if (dragState.current.dragging || now < dragState.current.suppressUntil) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    el.addEventListener("click", cancelClick, true);

    return () => {
      el.removeEventListener("click", cancelClick, true);
      sortableRef.current?.destroy();
      sortableRef.current = null;
    };
  }, [dropRef, dragDelayMs, useHandle]);

  useEffect(() => {
    if (!sortableRef.current) return;
    sortableRef.current.option("disabled", itemCount <= 1);
  }, [itemCount]);

  return { getId };
}
